// /app/frontend/src/pages/risk/dpia/steps/ProjectDetailsStep.jsx
import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Separator } from '../../../../components/ui/separator';
import { Calendar } from '../../../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

export default function ProjectDetailsStep({ formData, updateField, entities }) {

  return (
    <div className="space-y-6">
      {/* Reference Number - Manual Input */}
      <div className="p-4 bg-teal-50 dark:bg-teal-950/30 border border-teal-200 dark:border-teal-800 rounded-lg">
        <div className="space-y-2">
          <Label className="text-teal-800 dark:text-teal-300 font-medium">DPIA Reference Number</Label>
          <Input 
            value={formData.dpia_reference}
            onChange={(e) => updateField('dpia_reference', e.target.value)}
            placeholder="e.g., DPIA-2026-001"
            className="max-w-xs bg-white dark:bg-background"
            data-testid="dpia-reference-input"
          />
          <p className="text-xs text-teal-600 dark:text-teal-400">Enter your organization's reference number for this assessment</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Project Title *</Label>
          <Input 
            value={formData.project_title}
            onChange={(e) => updateField('project_title', e.target.value)}
            placeholder="Enter project title"
            data-testid="project-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Department</Label>
          <Input 
            value={formData.department}
            onChange={(e) => updateField('department', e.target.value)}
            placeholder="Enter department"
            data-testid="department-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Project Owner</Label>
          <Input 
            value={formData.project_owner}
            onChange={(e) => updateField('project_owner', e.target.value)}
            placeholder="Name of project owner"
            data-testid="project-owner-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Entity *</Label>
          <Select value={formData.entity_id} onValueChange={(v) => updateField('entity_id', v)}>
            <SelectTrigger data-testid="entity-select">
              <SelectValue placeholder="Select entity" />
            </SelectTrigger>
            <SelectContent>
              {entities.map(entity => (
                <SelectItem key={entity.id} value={entity.id}>{entity.entity_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />
      <h4 className="font-medium">Project Lead</h4>
      <div className="space-y-2">
        <Label>Name</Label>
        <Input 
          value={formData.project_lead_name}
          onChange={(e) => updateField('project_lead_name', e.target.value)}
          placeholder="Project lead name"
          data-testid="project-lead-name-input"
        />
      </div>

      <Separator />
      <h4 className="font-medium">Person Completing Assessment</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input 
            value={formData.person_completing_name}
            onChange={(e) => updateField('person_completing_name', e.target.value)}
            placeholder="Your name"
            data-testid="person-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Position</Label>
          <Input 
            value={formData.person_completing_position}
            onChange={(e) => updateField('person_completing_position', e.target.value)}
            placeholder="Your position"
            data-testid="person-position-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Details</Label>
          <Input 
            value={formData.contact_details}
            onChange={(e) => updateField('contact_details', e.target.value)}
            placeholder="Email or phone"
            data-testid="contact-details-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Date Submitted</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal"
                data-testid="date-submitted-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date_submitted ? format(new Date(formData.date_submitted), 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.date_submitted ? new Date(formData.date_submitted) : undefined}
                onSelect={(date) => updateField('date_submitted', date ? format(date, 'yyyy-MM-dd') : '')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}
